import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CONTACT } from '../config/products';
import { PRODUCT_IMAGES } from '../config/images';
import { Button } from './ui/button';
import { Globe, Share2, Home, MessageCircle, Menu, X, Palette, Image, Phone, Gift, BookOpen, Star } from 'lucide-react';

export const Header = ({ onColorFanClick }) => {
  const { t, toggleLanguage, isRTL } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'M\'art Microlit Calculator',
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled
      }
    }
  };

  const scrollToSection = (sectionId) => {
    setMenuOpen(false);

    // Golden Oz - navigate to dedicated route
    if (sectionId === 'golden-oz-section') {
      navigate('/golden-oz');
      return;
    }

    // If we're not on the homepage, navigate there first then scroll
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }

    // Special handling for color fan - auto-fill and scroll
    if (sectionId === 'colors-section' && onColorFanClick) {
      onColorFanClick();
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth'
      });
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const menuItems = [
    { id: 'colors-section', icon: Palette, labelHe: 'מניפת צבעים', labelEn: 'Color Fan' },
    { id: 'sample-pack-section', icon: Gift, labelHe: 'מארז היכרות', labelEn: 'Sample Pack' },
    { id: 'golden-oz-section', icon: Star, labelHe: 'גולדן עוז', labelEn: 'Golden Oz', highlight: true },
    { id: 'gallery-section', icon: Image, labelHe: 'גלריה', labelEn: 'Gallery' },
    { id: 'system-article', icon: BookOpen, labelHe: 'מאמר מערכת', labelEn: 'System Article' },
    { 
      id: 'whatsapp', 
      icon: MessageCircle, 
      labelHe: 'יצירת קשר', 
      labelEn: 'Contact',
      isExternal: true,
      href: `https://wa.me/${CONTACT.whatsapp}`
    },
  ];

  return (
    <>
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50"
      >
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          {/* Hamburger Menu Button */}
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-foreground"
              aria-label="Menu"
            >
              <AnimatePresence mode="wait">
                {menuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-5 h-5" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-5 h-5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </Button>
          </motion.div>

          {/* Center Logo - M'art Text Logo */}
          <motion.a 
            href="https://www.mart-mineral.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="absolute left-1/2 transform -translate-x-1/2"
            whileHover={{ scale: 1.05 }}
          >
            <div className="font-display text-xl sm:text-2xl tracking-wide text-foreground">
              <span className="font-bold">M</span>
              <span className="text-accent font-bold">'</span>
              <span className="font-bold">art</span>
            </div>
          </motion.a>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* WhatsApp Button - Enhanced with glow animation */}
            <motion.div 
              whileHover={{ scale: 1.15 }} 
              whileTap={{ scale: 0.9 }}
              className="relative"
            >
              {/* Outer glow ring */}
              <motion.div
                className="absolute -inset-1 rounded-full bg-success/20"
                animate={{ 
                  scale: [1, 1.4, 1], 
                  opacity: [0.4, 0, 0.4] 
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              />
              {/* Inner pulse */}
              <motion.div
                className="absolute inset-0 rounded-full bg-success/30"
                animate={{ 
                  scale: [1, 1.2, 1], 
                  opacity: [0.6, 0.2, 0.6] 
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: 0.3
                }}
              />
              <Button 
                variant="ghost" 
                size="icon"
                className="text-success hover:text-success hover:bg-success/10 relative z-10"
                asChild
              >
                <a 
                  href={`https://wa.me/${CONTACT.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  data-testid="whatsapp-header-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              </Button>
            </motion.div>

            <motion.div whileTap={{ scale: 0.95 }}>
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleLanguage}
                className="min-w-[65px] font-medium"
              >
                <Globe className="w-4 h-4 me-1.5" />
                {t('language')}
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: isRTL ? 300 : -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: isRTL ? 300 : -300, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`fixed top-16 ${isRTL ? 'right-0' : 'left-0'} w-72 h-[calc(100vh-4rem)] bg-card border-e border-border z-50 shadow-2xl`}
            >
              <div className="p-4 space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider px-3 mb-4">
                  {isRTL ? 'ניווט מהיר' : 'Quick Navigation'}
                </p>
                
                {menuItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.isExternal ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary transition-colors text-foreground group"
                        onClick={() => setMenuOpen(false)}
                      >
                        <motion.div
                          className="relative"
                          animate={item.id === 'whatsapp' ? { scale: [1, 1.1, 1] } : {}}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <item.icon className={`w-5 h-5 ${item.id === 'whatsapp' ? 'text-success' : 'text-accent'}`} />
                        </motion.div>
                        <span className="font-medium">{isRTL ? item.labelHe : item.labelEn}</span>
                        {item.id === 'whatsapp' && (
                          <motion.span
                            className="ms-auto text-xs text-success/70"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            {isRTL ? 'זמינים עכשיו' : 'Available now'}
                          </motion.span>
                        )}
                      </a>
                    ) : (
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-foreground text-start ${item.highlight ? 'bg-gradient-to-r from-[#E5B53A]/10 to-transparent hover:from-[#E5B53A]/20 border border-[#E5B53A]/25' : 'hover:bg-secondary'}`}
                      >
                        <item.icon className={`w-5 h-5 ${item.highlight ? 'text-[#B8862C] fill-[#E5B53A]/30' : 'text-accent'}`} />
                        <span className={`font-medium ${item.highlight ? 'text-[#3A2E18]' : ''}`}>{isRTL ? item.labelHe : item.labelEn}</span>
                      </button>
                    )}
                  </motion.div>
                ))}

                <div className="border-t border-border my-4" />

                {/* Back to Site */}
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <a
                    href="https://www.mart-mineral.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary transition-colors text-muted-foreground"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Home className="w-5 h-5" />
                    <span className="font-medium">{isRTL ? 'לאתר הראשי' : 'Main Website'}</span>
                  </a>
                </motion.div>

                {/* Share */}
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <button
                    onClick={() => {
                      handleShare();
                      setMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-secondary transition-colors text-muted-foreground text-start"
                  >
                    <Share2 className="w-5 h-5" />
                    <span className="font-medium">{isRTL ? 'שתף עמוד' : 'Share Page'}</span>
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
